import { notNullish, sortBy } from '@paragon/utilities';

import Line2D from './Line2D';
import Polygon2D from './Polygon2D';
import {
  distanceEquals,
  distanceGreaterThan,
  distanceGreaterThanOrEqual,
  distanceLessThan,
  distanceLessThanOrEqual,
} from './units';

export default class Span {
  readonly start: number;
  readonly end: number;

  constructor(distance1: number, distance2: number) {
    if (distanceEquals(distance1, distance2)) throw new Error('Span must have breadth.');

    if (distance1 <= distance2) {
      this.start = distance1;
      this.end = distance2;
    } else {
      this.start = distance2;
      this.end = distance1;
    }
  }

  static fromIntersectionsOfLineAndPolygon(line: Line2D, polygon: Polygon2D): Span[] {
    return polygon
      .findSegmentIntersectionsWithLine(line)
      .map((intersection) => {
        const baseDistanceAlong = intersection.basePoint.projectedSignedDistanceAlong(line);
        const endDistanceAlong = intersection.endPoint.projectedSignedDistanceAlong(line);
        return distanceEquals(baseDistanceAlong, endDistanceAlong)
          ? null
          : new Span(baseDistanceAlong, endDistanceAlong);
      })
      .filter(notNullish);
  }

  contains(distance: number) {
    return distanceGreaterThanOrEqual(distance, this.start) && distanceLessThanOrEqual(distance, this.end);
  }

  containsOnInside(distance: number) {
    return distanceGreaterThan(distance, this.start) && distanceLessThan(distance, this.end);
  }

  overlapsOrTouches(span: Span) {
    return this.contains(span.start) || this.contains(span.end);
  }

  overlaps(span: Span) {
    const spanIsBeforeStart = distanceLessThanOrEqual(span.end, this.start);
    const spansIsAfterEnd = distanceGreaterThanOrEqual(span.start, this.end);
    return !(spanIsBeforeStart || spansIsAfterEnd);
  }

  intersect(span: Span) {
    if (!this.overlaps(span)) return null;
    return new Span(Math.max(this.start, span.start), Math.min(this.end, span.end));
  }

  /** The output is guaranteed to be sorted and reduced. */
  subtract(span: Span) {
    if (!this.overlaps(span)) return [this];

    const startSpan = distanceGreaterThan(span.start, this.start) ? new Span(this.start, span.start) : null;
    const endSpan = distanceLessThan(span.end, this.end) ? new Span(span.end, this.end) : null;
    return [startSpan, endSpan].filter(notNullish);
  }

  splitAt(distance: number) {
    if (!this.containsOnInside(distance)) return [this];
    return [new Span(this.start, distance), new Span(distance, this.end)];
  }

  growIntoAdjacentSpan(span: { onLeft?: MaybeSpan; onRight?: MaybeSpan }) {
    if (span.onLeft && span.onRight) return new Span(span.onLeft.start, span.onRight.end);

    if (span.onLeft) return new Span(span.onLeft.start, this.end);
    if (span.onRight) return new Span(this.start, span.onRight.end);

    return new Span(this.start, this.end);
  }

  growIntoAdjacentSpans = this.growIntoAdjacentSpan;
}

type MaybeSpan = Span | null | undefined;

export function reduceAndSortSpans(spans: readonly Span[]) {
  const reducedSpans: Span[] = [];
  if (spans.length === 0) return reducedSpans;

  const sortedSpans = sortBy(spans, (span) => span.start);

  const firstSpan = sortedSpans.shift()!;
  let { start, end } = firstSpan;

  while (sortedSpans.length > 0) {
    const span = sortedSpans.shift()!;

    if (distanceLessThanOrEqual(span.start, end)) {
      end = Math.max(end, span.end);
    } else {
      reducedSpans.push(new Span(start, end));
      start = span.start;
      end = span.end;
    }
  }

  reducedSpans.push(new Span(start, end));

  return reducedSpans;
}

/** Assumes `minuend` and `subtrahend` are sorted and reduced. The output is guaranteed to be sorted and reduced. */
export const subtractSpanSets = (minuend: readonly Span[], subtrahend: readonly Span[]) =>
  subtrahend.reduce((min, sub) => subtractSpanFromSpanSet(min, sub), minuend);

/** Assumes `minuend` is sorted and reduced. The output is guaranteed to be sorted and reduced. */
const subtractSpanFromSpanSet = (minuend: readonly Span[], subtrahend: Span) =>
  minuend.flatMap((min) => min.subtract(subtrahend));

export interface SpanLike<T> {
  readonly start: number;
  readonly end: number;
  readonly intersect: (other: T) => T | null;
}

export function intersectSpanSets<T extends SpanLike<T>>(spansA: readonly T[], spansB: readonly T[]) {
  const sortedSpansA = sortBy(spansA, (span) => span.start);
  const sortedSpansB = sortBy(spansB, (span) => span.start);

  const intersectionSpans: T[] = [];

  let currentSpanA = sortedSpansA.shift();
  let currentSpanB = sortedSpansB.shift();

  while (currentSpanA && currentSpanB) {
    const intersection = currentSpanA.intersect(currentSpanB);
    if (intersection) {
      intersectionSpans.push(intersection);
    }

    if (currentSpanA.end < currentSpanB.end) {
      currentSpanA = sortedSpansA.shift();
    } else {
      currentSpanB = sortedSpansB.shift();
    }
  }

  return intersectionSpans;
}
